import React, { useEffect, useState } from "react";
import { Plus, Trash2, Eye, Pencil, Link2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { statusClass, todayISO, toast } from "../../lib/utils";
import RiskScheduleLinker from "../RiskScheduleLinker";

const TYPES = ["All", "Risk", "Assumption", "Issue", "Dependency"];
const typeTone = (t) => t === "Risk" ? "amber" : t === "Issue" ? "red" : t === "Assumption" ? "blue" : "purple";

export default function RAID() {
  const { doc, mutate, readOnly } = useProject();
  const { id: projectId } = useParams();
  const [filter, setFilter] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  // Draft that mirrors form fields so RiskScheduleLinker can react to typing.
  const [addDraft, setAddDraft]   = useState({ type: "Risk", category: "Technical", title: "", desc: "", linkedScheduleIds: [] });
  const [editDraft, setEditDraft] = useState(null);

  // Seed the reactive edit-draft when a RAID row is opened for editing.
  // Doing this in an effect (not during render) keeps StrictMode happy.
  useEffect(() => {
    if (!editing) { setEditDraft(null); return; }
    setEditDraft({
      _id: editing.id,
      type: editing.type,
      category: editing.category || "Technical",
      title: editing.title,
      desc: editing.desc || "",
      linkedScheduleIds: editing.linkedScheduleIds || [],
    });
  }, [editing]);

  const list = filter === "All" ? doc.raids : doc.raids.filter(r => r.type === filter);

  const addRAID = () => {
    const g = (k) => document.getElementById("nr-" + k)?.value;
    const title = g("title");
    if (!title) return toast("Title required");
    const type = g("type");
    const prefix = { Risk: "R", Assumption: "A", Issue: "I", Dependency: "D" }[type] || "X";
    const count = doc.raids.filter(r => r.type === type).length + 1;
    mutate(d => ({
      ...d, raids: [...d.raids, {
        id: "R" + Date.now(), type,
        ref: `${prefix}-${String(count).padStart(3, "0")}`, title,
        desc: g("desc") || "",
        category: g("cat"),
        probability: g("prob"),
        impact: g("imp"),
        score: g("imp"),
        owner: g("own") || "TBD",
        status: g("stat"),
        mitigation: g("mit") || "",
        linkedScheduleIds: addDraft.linkedScheduleIds || [],
        created: todayISO(), updated: todayISO(),
      }],
    }));
    setShowAdd(false);
    setAddDraft({ type: "Risk", category: "Technical", title: "", desc: "", linkedScheduleIds: [] });
  };

  const saveEdit = () => {
    if (!editing) return;
    const g = (k) => document.getElementById("er-" + k)?.value;
    mutate(d => ({
      ...d,
      raids: d.raids.map(r => r.id === editing.id ? {
        ...r,
        type:        g("type")  ?? r.type,
        title:       g("title") ?? r.title,
        desc:        g("desc")  ?? r.desc,
        category:    g("cat")   ?? r.category,
        probability: g("prob")  ?? r.probability,
        impact:      g("imp")   ?? r.impact,
        score:       g("imp")   ?? r.score,
        owner:       g("own")   ?? r.owner,
        status:      g("stat")  ?? r.status,
        mitigation:  g("mit")   ?? r.mitigation,
        linkedScheduleIds: editDraft?.linkedScheduleIds ?? (r.linkedScheduleIds || []),
        updated:     todayISO(),
      } : r),
    }));
    setEditing(null);
    toast("Saved");
  };

  const removeRAID = (id) => {
    if (!window.confirm("Delete this RAID entry?")) return;
    mutate(d => ({ ...d, raids: d.raids.filter(r => r.id !== id) }));
    setViewing(null);
    setEditing(null);
  };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Risks · Assumptions · Issues · Dependencies</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">RAID Log</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {TYPES.map(t => (
            <button key={t} className={`btn btn-sm ${filter === t ? "btn-primary" : "btn-secondary"}`} onClick={() => setFilter(t)} data-testid={`filter-raid-${t}`}>{t}</button>
          ))}
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="btn-add-raid" disabled={readOnly} style={readOnly ? { display: "none" } : {}}><Plus size={12}/> Add</button>
        </div>
      </div>

      <AIPanel title="Risk Analysis" section="risk" buildPrompt={() => prompts.riskSummary(doc)} buttonLabel="Analyze"/>

      <div className="card overflow-x-auto">
        <table className="tbl">
          <thead><tr>
            <th>Ref</th><th>Type</th><th>Title</th><th>Prob</th><th>Impact</th><th>Owner</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id}>
                <td className="font-mono text-xs text-zinc-500">{r.ref}</td>
                <td><Pill tone={typeTone(r.type)}>{r.type}</Pill></td>
                <td className="max-w-[300px]">
                  <div className="text-sm font-medium text-zinc-900">{r.title}</div>
                  <div className="text-[11px] text-zinc-500 line-clamp-1">{(r.desc || "").slice(0, 70)}{(r.desc || "").length > 70 ? "…" : ""}</div>
                  {(r.linkedScheduleIds || []).length > 0 && (() => {
                    const firstId = r.linkedScheduleIds[0];
                    const item = [...(doc.milestones || []), ...(doc.tasks || [])].find(x => (x.id || x.name) === firstId);
                    if (!item) return null;
                    const dateStr = item.date || item.end;
                    const daysAway = dateStr ? Math.floor((new Date(dateStr) - new Date()) / 86400000) : null;
                    const label = daysAway === null ? "" : daysAway < 0 ? `${-daysAway}d overdue` : `${daysAway}d away`;
                    return (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800" data-testid={`raid-link-chip-${r.id}`}>
                        <Link2 size={9} /> {item.name.slice(0, 24)}{label ? ` · ${label}` : ""}
                        {r.linkedScheduleIds.length > 1 && <span className="ml-0.5">+{r.linkedScheduleIds.length - 1}</span>}
                      </span>
                    );
                  })()}
                </td>
                <td><Pill tone={statusClass(r.probability).replace("pill-","")}>{r.probability}</Pill></td>
                <td><Pill tone={statusClass(r.impact).replace("pill-","")}>{r.impact}</Pill></td>
                <td className="font-mono text-xs text-zinc-500">{r.owner}</td>
                <td><Pill tone={statusClass(r.status).replace("pill-","")}>{r.status}</Pill></td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-ghost btn-sm" onClick={() => setViewing(r)} data-testid={`btn-view-raid-${r.id}`} title="View"><Eye size={11}/></button>
                    {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(r)} data-testid={`btn-edit-raid-${r.id}`} title="Edit"><Pencil size={11}/></button>}
                    {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => removeRAID(r.id)} data-testid={`btn-del-raid-${r.id}`} title="Delete"><Trash2 size={11}/></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add RAID Entry"
             footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                       <button className="btn btn-primary" onClick={addRAID} data-testid="btn-save-raid">Add Entry</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select id="nr-type" className="fld" defaultValue="Risk" onChange={e => setAddDraft(d => ({ ...d, type: e.target.value }))}>{["Risk","Assumption","Issue","Dependency"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Category">
            <select id="nr-cat" className="fld" defaultValue="Technical" onChange={e => setAddDraft(d => ({ ...d, category: e.target.value }))}>{["Technical","Resource","Process","Financial","Compliance","External"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Title" span={2}><input id="nr-title" className="fld" placeholder="Concise title" data-testid="input-raid-title" onChange={e => setAddDraft(d => ({ ...d, title: e.target.value }))}/></Field>
          <Field label="Description" span={2}><textarea id="nr-desc" className="fld" rows={3} onChange={e => setAddDraft(d => ({ ...d, desc: e.target.value }))}/></Field>
          <Field label="Probability">
            <select id="nr-prob" className="fld" defaultValue="Medium">{["Low","Medium","High","N/A"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Impact">
            <select id="nr-imp" className="fld" defaultValue="High">{["Low","Medium","High","Critical"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Owner"><input id="nr-own" className="fld"/></Field>
          <Field label="Status">
            <select id="nr-stat" className="fld" defaultValue="Open">{["Open","Monitoring","Escalated","Active","On Track","Closed"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Mitigation / Response" span={2}><textarea id="nr-mit" className="fld" rows={2}/></Field>
          {addDraft.type === "Risk" && (
            <div className="col-span-2">
              <RiskScheduleLinker
                projectId={projectId}
                raid={addDraft}
                doc={doc}
                value={addDraft.linkedScheduleIds}
                onChange={(ids) => setAddDraft(d => ({ ...d, linkedScheduleIds: ids }))}
              />
            </div>
          )}
        </div>
      </Modal>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing ? `${viewing.type} · ${viewing.ref}` : ""}>
        {viewing && (
          <>
            <div className="text-lg font-semibold text-zinc-900 mb-2">{viewing.title}</div>
            <div className="text-sm text-zinc-700 leading-relaxed mb-3">{viewing.desc}</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Status"><Pill tone={statusClass(viewing.status).replace("pill-","")}>{viewing.status}</Pill></Field>
              <Field label="Impact"><Pill tone={statusClass(viewing.impact).replace("pill-","")}>{viewing.impact}</Pill></Field>
            </div>
            <Field label="Mitigation"><div className="text-sm text-zinc-700 bg-white border border-[var(--border)] rounded-sm p-3">{viewing.mitigation || "—"}</div></Field>
            <div className="flex justify-between items-center pt-2">
              <span className="font-mono text-[10px] text-zinc-500">Updated: {viewing.updated}</span>
              <button className="btn btn-danger" onClick={() => removeRAID(viewing.id)} disabled={readOnly} style={readOnly ? { display: "none" } : {}}><Trash2 size={11}/> Delete</button>
            </div>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => { setEditing(null); setEditDraft(null); }} title={editing ? `Edit ${editing.type} · ${editing.ref}` : ""}
             footer={<><button className="btn btn-ghost" onClick={() => { setEditing(null); setEditDraft(null); }}>Cancel</button>
                       <button className="btn btn-primary" onClick={saveEdit} data-testid="btn-save-edit-raid">Save</button></>}>
        {editing && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select id="er-type" className="fld" defaultValue={editing.type} onChange={e => setEditDraft(d => ({ ...d, type: e.target.value }))}>{["Risk","Assumption","Issue","Dependency"].map(o=><option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Category">
              <select id="er-cat" className="fld" defaultValue={editing.category || "Technical"} onChange={e => setEditDraft(d => ({ ...d, category: e.target.value }))}>{["Technical","Resource","Process","Financial","Compliance","External"].map(o=><option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Title" span={2}><input id="er-title" className="fld" defaultValue={editing.title} onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}/></Field>
            <Field label="Description" span={2}><textarea id="er-desc" className="fld" rows={3} defaultValue={editing.desc} onChange={e => setEditDraft(d => ({ ...d, desc: e.target.value }))}/></Field>
            <Field label="Probability">
              <select id="er-prob" className="fld" defaultValue={editing.probability}>{["Low","Medium","High","N/A"].map(o=><option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Impact">
              <select id="er-imp" className="fld" defaultValue={editing.impact}>{["Low","Medium","High","Critical"].map(o=><option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Owner"><input id="er-own" className="fld" defaultValue={editing.owner}/></Field>
            <Field label="Status">
              <select id="er-stat" className="fld" defaultValue={editing.status}>{["Open","Monitoring","Escalated","Active","On Track","Closed"].map(o=><option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Mitigation / Response" span={2}><textarea id="er-mit" className="fld" rows={2} defaultValue={editing.mitigation}/></Field>
            {editDraft?.type === "Risk" && (
              <div className="col-span-2">
                <RiskScheduleLinker
                  projectId={projectId}
                  raid={editDraft}
                  doc={doc}
                  value={editDraft?.linkedScheduleIds || []}
                  onChange={(ids) => setEditDraft(d => ({ ...d, linkedScheduleIds: ids }))}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
